import React, { useEffect, useRef, useCallback } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact';
        }
      ) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: () => void;
  theme?: 'light' | 'dark' | 'auto';
}

export const Turnstile: React.FC<TurnstileProps> = ({
  siteKey,
  onVerify,
  onError,
  theme = 'auto',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | undefined>(undefined);
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);

  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;

  const renderWidget = useCallback(() => {
    if (!ref.current || !window.turnstile) return;
    if (widgetIdRef.current) return; // already rendered

    widgetIdRef.current = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      callback: (token) => onVerifyRef.current?.(token),
      'error-callback': () => onErrorRef.current?.(),
      'expired-callback': () => onErrorRef.current?.(),
      theme,
      size: 'normal',
    });
  }, [siteKey, theme]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // clear placeholder on re-mount
    el.innerHTML = '';

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]');
    if (existing) {
      existing.addEventListener('load', renderWidget, { once: true });
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.dataset.turnstile = 'true';
      script.onload = renderWidget;
      document.body.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      if (el) el.innerHTML = '';
      widgetIdRef.current = undefined;
    };
  }, [renderWidget]);

  return <div ref={ref} className="flex justify-center" />;
};
